import { useEffect, useRef, useState } from 'react';

export default function AccessSection() {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (headingRef.current) {
      observer.observe(headingRef.current);
    }

    return () => {
      if (headingRef.current) {
        observer.unobserve(headingRef.current);
      }
    };
  }, []);

  return (
    <section id="access" className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12 sm:mb-16">
          <h2 
            ref={headingRef}
            className={`text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-4 sm:mb-6 transition-all duration-1000 ease-out ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'
            }`}
          >
            アクセス・教室情報
          </h2>
          <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
            大宮駅から徒歩圏内、通いやすい立地です
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
          {/* 教室所在地 */}
          <div className="bg-white p-6 sm:p-8 rounded-2xl sm:rounded-3xl shadow-lg border-2 border-gray-100">
            <div className="flex items-center gap-3 mb-4 sm:mb-6">
              <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-gradient-to-br from-orange-500 to-orange-600 rounded-full">
                <i className="ri-map-pin-line text-xl sm:text-2xl text-white"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-800">教室所在地</h3>
            </div>

            <div className="space-y-4 sm:space-y-6">
              <div>
                <h4 className="text-xs sm:text-sm font-bold text-gray-700 mb-2">住所</h4>
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                  〒330-0841<br />
                  埼玉県さいたま市大宮区東町1丁目46<br />
                  オガワとうちゅうビル2F
                </p>
              </div>

              <div>
                <h4 className="text-xs sm:text-sm font-bold text-gray-700 mb-2">アクセス</h4>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <i className="ri-train-line text-base sm:text-lg text-orange-500 flex-shrink-0 mt-0.5"></i>
                    <span className="text-sm sm:text-base text-gray-700">JR京浜東北・根岸線 / 大宮駅より徒歩12分</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-train-line text-base sm:text-lg text-orange-500 flex-shrink-0 mt-0.5"></i>
                    <span className="text-sm sm:text-base text-gray-700">JR埼京線 / 大宮駅より徒歩12分</span>
                  </li>
                </ul>
              </div>

              <div className="bg-orange-50 p-3 sm:p-4 rounded-xl sm:rounded-2xl">
                <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                  駅から近く、学校帰りやお仕事帰りにも通いやすい立地です
                </p>
              </div>
            </div>
          </div>

          {/* 営業時間・お問い合わせ */}
          <div className="bg-white p-6 sm:p-8 rounded-2xl sm:rounded-3xl shadow-lg border-2 border-gray-100">
            <div className="flex items-center gap-3 mb-4 sm:mb-6">
              <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-gradient-to-br from-orange-500 to-orange-600 rounded-full">
                <i className="ri-time-line text-xl sm:text-2xl text-white"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-800">営業時間・お問い合わせ</h3>
            </div>

            <div className="space-y-4 sm:space-y-6">
              <div>
                <h4 className="text-xs sm:text-sm font-bold text-gray-700 mb-2">営業時間</h4>
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                  平日・土日祝:10:00〜22:00<br />
                  定休日:なし
                </p>
              </div>

              <div>
                <h4 className="text-xs sm:text-sm font-bold text-gray-700 mb-2">電話番号</h4>
                <a
                  href="tel:08057593875"
                  className="text-base sm:text-lg font-bold text-orange-500 hover:text-orange-600 transition-colors duration-200 cursor-pointer"
                >
                  080-5759-3875
                </a>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">受付時間:10:00〜22:00</p>
              </div>

              <div>
                <h4 className="text-xs sm:text-sm font-bold text-gray-700 mb-2">メールアドレス</h4>
                <a
                  href="mailto:musicschool@s-i-z-e.net"
                  className="text-sm sm:text-base text-orange-500 hover:text-orange-600 transition-colors duration-200 cursor-pointer break-all"
                >
                  musicschool@s-i-z-e.net
                </a>
              </div>

              <div className="bg-orange-50 p-3 sm:p-4 rounded-xl sm:rounded-2xl">
                <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                  体験レッスンのご予約は、お電話・メール・LINEで受付中
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Google Map */}
        <div className="mt-8 sm:mt-10 md:mt-12">
          <div className="bg-white p-3 sm:p-4 rounded-2xl sm:rounded-3xl shadow-lg border-2 border-gray-100">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3229.8!2d139.6267!3d35.9067!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6018c1b5e5e5e5e5%3A0x5e5e5e5e5e5e5e5e!2z44CSMzMwLTA4NDEg5Z-8546J55yM44GV44GE44Gf44G-5biC5aSn5a6u5Yy65p2x55S677yR5LiB55uu77yU77yW!5e0!3m2!1sja!2sjp!4v1234567890"
              width="100%"
              height="350"
              style={{ border: 0, borderRadius: '1rem' }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Size Music School Location"
              className="sm:h-[400px] md:h-[450px]"
            ></iframe>
          </div>
        </div>
      </div>
    </section>
  );
}
